/**
 * Calibration data — "comments a human overrode the AI on" — with a fixture path for
 * `?moderationData=mock` (the same param as the queue, so one URL previews both tabs).
 *
 * Wraps `Query.moderationOverrides`: each applicable AI verdict paired with the first human
 * decision that answered it on the same text, kept where the two disagree. `since` windows
 * on the verdict's time; `facets` count every verdict in the window per category and ignore
 * the `direction` / `category` item filters. Rows are keyed by `id` (the human decision's
 * ledger event) — one comment can appear more than once after edits, so `itemId` is not a
 * row key.
 *
 * Mock fixtures live under `lib/gui/components/moderation-calibration/mock/` for the same
 * chunk-isolation reason as the queue's (see `useModerationQueue.ts`). The mock filters the
 * fixture items client-side the way the server would and returns its facets as-is — the
 * facets are a fixed picture there, not a function of the window.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  type ModerationCategory,
  type ModerationOverrideDirection,
  ModerationSurface,
} from '../../graphql-types'
import {
  useModerationOverridesQuery,
  type ModerationOverridesQuery,
} from './queries/ModerationOverridesQuery.generated'
import useModerationMockMode from './useModerationMockMode'

/** One override row, exactly as the query selects it. */
export type ModerationOverrideItem =
  ModerationOverridesQuery['moderationOverrides']['items'][number]
export type ModerationOverrideFacet =
  ModerationOverridesQuery['moderationOverrides']['facets'][number]
export type ModerationOverrideComment = ModerationOverrideItem['comment']

export const MODERATION_OVERRIDES_PAGE_SIZE = 50

export interface UseModerationOverridesOptions {
  page: number
  pageSize: number
  /** ISO timestamp: only verdicts issued at or after it. Undefined = all time. */
  since?: string
  direction?: ModerationOverrideDirection
  category?: ModerationCategory
}

export interface UseModerationOverridesResult {
  items: ModerationOverrideItem[]
  facets: ModerationOverrideFacet[]
  total: number
  loading: boolean
  error: Error | null
  refetch: () => Promise<void>
  isMock: boolean
}

const NO_ITEMS: ModerationOverrideItem[] = []
const NO_FACETS: ModerationOverrideFacet[] = []

export default function useModerationOverrides ({
  page,
  pageSize,
  since,
  direction,
  category,
}: UseModerationOverridesOptions): UseModerationOverridesResult {
  const mockMode = useModerationMockMode()
  const isMock = mockMode !== null

  const [mockItems, setMockItems] = useState<ModerationOverrideItem[] | null>(null)
  const [mockFacets, setMockFacets] = useState<ModerationOverrideFacet[]>(NO_FACETS)
  const [mockError, setMockError] = useState<Error | null>(null)

  const loadMock = useCallback(async (): Promise<void> => {
    const fixtures = await import('../../gui/components/moderation-calibration/mock/fixtures')
    if (mockMode === 'mock-error') {
      setMockError(fixtures.MOCK_OVERRIDES_ERROR)
      return
    }
    setMockError(null)
    setMockItems(fixtures.buildMockOverrideItems())
    setMockFacets(fixtures.MOCK_OVERRIDE_FACETS)
  }, [mockMode])

  useEffect(() => {
    if (!isMock || mockMode === 'mock-loading') return
    loadMock().catch(() => {
      setMockError(new Error('Mock: failed to load the calibration fixtures'))
    })
  }, [isMock, mockMode, loadMock])

  const visibleMockItems = useMemo(() => {
    let visible = mockItems ?? NO_ITEMS
    if (since !== undefined) {
      const cutoff = new Date(since).getTime()
      visible = visible.filter((item) => new Date(item.aiDecidedAt).getTime() >= cutoff)
    }
    if (direction !== undefined) {
      visible = visible.filter((item) => item.direction === direction)
    }
    if (category !== undefined) {
      visible = visible.filter((item) => item.aiCategory === category)
    }
    return visible
  }, [mockItems, since, direction, category])

  const { data, loading, error, refetch } = useModerationOverridesQuery({
    variables: {
      surface: ModerationSurface.Comment,
      since,
      direction,
      category,
      limit: pageSize,
      offset: page * pageSize,
    },
    notifyOnNetworkStatusChange: true,
    skip: isMock,
  })

  const refetchOverrides = useCallback(async (): Promise<void> => {
    if (isMock) {
      await loadMock()
      return
    }
    await refetch()
  }, [isMock, loadMock, refetch])

  return useMemo(() => {
    if (isMock) {
      return {
        items: visibleMockItems.slice(page * pageSize, (page + 1) * pageSize),
        facets: mockFacets,
        total: visibleMockItems.length,
        loading: mockMode === 'mock-loading' || (!mockItems && !mockError),
        error: mockError,
        refetch: refetchOverrides,
        isMock: true,
      }
    }
    return {
      items: data?.moderationOverrides.items ?? NO_ITEMS,
      facets: data?.moderationOverrides.facets ?? NO_FACETS,
      total: data?.moderationOverrides.total ?? 0,
      loading,
      error: error ?? null,
      refetch: refetchOverrides,
      isMock: false,
    }
  }, [
    isMock,
    mockMode,
    mockItems,
    mockFacets,
    visibleMockItems,
    mockError,
    page,
    pageSize,
    data,
    loading,
    error,
    refetchOverrides,
  ])
}
